import { useEffect, useMemo, useState } from "react";
import { Table, ScrollArea, Text, Alert, LoadingOverlay } from "@mantine/core";
import SearchInput from "@app/components/DashAdmin/SearchInput";
import { sortData, Th } from "../../components/DashAdmin/DashAdmin.helpers";
import { gql, useQuery } from "@apollo/client";
import ButtonMore from "@app/components/DashAdmin/ButtonMore";
import SecondAssessBtn from "@app/components/DashAdmin/SecondAssessBtn";
import { IconAlertCircle } from "@tabler/icons";
import ApplicationStatusChip from "@app/components/ApplicationStatusChip";

export interface LegalForm {
  name: string;
}

interface RowData {
  name: string;
  address: string;
  creationYear: number;
  activity: string;
  legalForm: LegalForm;
}

const BUSINESSES_QUERY = gql`
  query Businesses {
    businesses {
      nodes {
        ... on Business {
          id
          name
          address
          creationYear
          activity
          legalForm {
            name
          }
          answers {
            status
            scores {
              scoreT1
              scoreT2
              scoreT3
              jury {
                id
                email
              }
            }
          }
        }
      }
    }
    options {
      name
      value
    }
  }
`;

interface Option {
  name: string;
  value: string;
}

enum AnswersStatus {
  PENDING = "PENDING",
  SUBMITTED = "SUBMITTED",
  ACCEPTED_PITCH = "ACCEPTED_PITCH",
  REFUSED_PITCH = "REFUSED_PITCH",
}

export default function TableJuries() {
  const { loading, error, data } = useQuery(BUSINESSES_QUERY);

  const [search, setSearch] = useState("");
  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  // Get the current stage
  const options: Option[] = data?.options || [];
  const currentStageOption = options.find(
    (item) => item.name === "current_stage"
  );
  const currentStageId = parseInt(currentStageOption?.value || "1");

  // Get the applications
  const businesses = useMemo(() => data?.businesses?.nodes || [], [data]);

  useEffect(() => {
    if (Array.isArray(businesses) && sortedData.length === 0 && search === "") {
      setSortedData(businesses);
    }
  }, [businesses, search, sortedData.length]);

  const setSorting = (field: keyof RowData) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    const data = businesses.map((business) => {
      return {
        ...business,
        legalForm: business.legalForm?.name,
      };
    });
    setSortedData(sortData(data, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    const _tmp = sortData(data.businesses, {
      sortBy,
      reversed: reverseSortDirection,
      search: value,
    });
    setSortedData(_tmp);
  };

  const rows = sortedData.map((row) => {
    const answers = row.answers;
    const status = answers?.status || AnswersStatus.PENDING;
    const hasVoted = Array.isArray(answers?.scores) && answers.scores.length;

    return (
      <tr key={row.name}>
        <td>
          <div>
            {row.name}
            {currentStageId === 1 && !!hasVoted && (
              <span className="text-[0.5rem] bg-green-500 text-white font-bold ml-1 px-1.5 py-1 rounded-full">
                Voté
              </span>
            )}
          </div>
          <div className="text-gray-500 text-xs italic">{row.address}</div>
        </td>
        <td>{row.creationYear}</td>
        <td>{row.legalForm?.name || row.legalForm}</td>
        <td>{row.activity}</td>
        <td>
          <ApplicationStatusChip status={status} />
        </td>
        <td className="">
          {currentStageId === 2 && status === AnswersStatus.ACCEPTED_PITCH ? (
            <SecondAssessBtn id={row.id} name={row.name} />
          ) : (
            <ButtonMore id={row.id} name={row.name} />
          )}
        </td>
      </tr>
    );
  });

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <h1 className="text-2xl font-semibold">Liste des candidats</h1>
        <SearchInput search={search} handleSearchChange={handleSearchChange} />
      </div>
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Oups !" color="red">
          Une erreur s&apos;est produite lors de l&apos;extraction des données
          nécessaires. L&apos;équipe de support est prévenue, veuillez réessayer
          plus tard.
        </Alert>
      )}
      <ScrollArea>
        <LoadingOverlay visible={loading} overlayBlur={2} />
        <Table
          horizontalSpacing="md"
          verticalSpacing="xs"
          sx={{ tableLayout: "fixed", minWidth: 700 }}
          className="!border !border-grey-light"
        >
          <thead>
            <tr>
              <Th
                sorted={sortBy === "name"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("name")}
              >
                Raison social
              </Th>

              <Th
                sorted={sortBy === "creationYear"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("creationYear")}
              >
                Année de création
              </Th>
              <Th
                sorted={sortBy === "legalForm"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("legalForm")}
              >
                Forme juridique
              </Th>
              <Th
                sorted={sortBy === "activity"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("activity")}
              >
                Activité
              </Th>
              <Th
                sorted={sortBy === "address"}
                reversed={reverseSortDirection}
                onSort={() => setSorting("address")}
              >
                Statut
              </Th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={6}>
                  <Text weight={500} align="center">
                    Aucune information disponible.
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
    </>
  );
}
